import {
  Box,
  Card,
  CardContent,
  Stack,
  Step,
  stepClasses,
  StepIndicator,
  stepIndicatorClasses,
  Stepper,
  Typography,
  useTheme,
} from '@mui/joy';
import { Check, X } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from 'core/elements';
import { QuoteStatus } from 'quotes/enums';
import { ICustomerQuote } from 'quotes/types';
import { Color, Level, Orientation, TooltipPosition, Variant } from 'theme/enums';

import { getExternalOrderStatusLabel } from './ExternalOrderStatus';

const ACTIVE_STEP_DOT_SIZE = 10;

enum OrderStepStatus {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  FAILED = 'failed',
  PENDING = 'pending',
}

interface IOrderStep {
  label: string;
  status: OrderStepStatus;
}

interface IProps {
  children: JSX.Element;
  quote: ICustomerQuote;
  // Controlled only by the stories, which show the bubble without a hover.
  open?: boolean;
}

// The step in progress is a ring with a dot inside; only finished steps are filled.
const STEP_INDICATORS: Record<OrderStepStatus, { color: Color; variant: Variant }> = {
  [OrderStepStatus.ACTIVE]: { color: Color.SUCCESS, variant: Variant.OUTLINED },
  [OrderStepStatus.COMPLETED]: { color: Color.SUCCESS, variant: Variant.SOLID },
  [OrderStepStatus.FAILED]: { color: Color.DANGER, variant: Variant.SOLID },
  [OrderStepStatus.PENDING]: { color: Color.NEUTRAL, variant: Variant.OUTLINED },
};

export const OrderStatusTooltip = ({ children, quote, open }: IProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const steps = useMemo((): IOrderStep[] => {
    const orderingStep: IOrderStep = {
      label: t('labelOrderStepOrdering'),
      status: OrderStepStatus.COMPLETED,
    };

    const awaitingResponseStep: IOrderStep = {
      label: t('labelOrderStepAwaitingResponse'),
      status:
        quote.status === QuoteStatus.ORDER_ORDERING
          ? OrderStepStatus.ACTIVE
          : OrderStepStatus.COMPLETED,
    };

    const getThirdStep = (): IOrderStep => {
      if (quote.status === QuoteStatus.ORDER_ORDERING) {
        return {
          label: t('labelOrderStepOrderCreated'),
          status: OrderStepStatus.PENDING,
        };
      }

      if (quote.status === QuoteStatus.ORDER_ORDER_FAILED) {
        return {
          label: t('labelOrderStepOrderFailed'),
          status: OrderStepStatus.FAILED,
        };
      }

      const externalLabel = quote.externalStatus
        ? ` - ${getExternalOrderStatusLabel(quote.externalStatus)}`
        : '';

      return {
        label: `${t('labelOrderStepOrderCreated')}${externalLabel}`,
        status: OrderStepStatus.COMPLETED,
      };
    };

    return [orderingStep, awaitingResponseStep, getThirdStep()];
  }, [quote.status, quote.externalStatus, t]);

  const tooltipTitleKey = useMemo(() => {
    if (quote.status === QuoteStatus.ORDER_ORDER_FAILED) {
      return 'tooltipOrderStatusFailedTitle';
    }

    if (quote.status === QuoteStatus.ORDER_ORDERED) {
      return 'tooltipOrderStatusSuccessTitle';
    }

    return 'tooltipOrderStatusTitle';
  }, [quote.status]);

  const tooltipSubtitleKey = useMemo(() => {
    if (quote.status === QuoteStatus.ORDER_ORDER_FAILED) {
      return 'tooltipOrderStatusFailedSubtitle';
    }

    if (quote.status === QuoteStatus.ORDER_ORDERED) {
      return 'tooltipOrderStatusSuccessSubtitle';
    }

    return 'tooltipOrderStatusSubtitle';
  }, [quote.status]);

  return (
    <Tooltip
      title={
        <Stack sx={{ gap: 1.5, minWidth: 340 }}>
          <Stack sx={{ gap: 0.5 }}>
            <Typography level={Level.TITLE_MD} sx={{ fontWeight: 600 }}>
              {t(tooltipTitleKey)}
            </Typography>

            <Typography level={Level.BODY_SM}>{t(tooltipSubtitleKey)}</Typography>

            {QuoteStatus.ORDER_ORDERING === quote.status && (
              <Typography level={Level.TITLE_SM}>{t('tooltipOrderStatusDoNotRefresh')}</Typography>
            )}
          </Stack>

          {quote.status === QuoteStatus.ORDER_ORDER_FAILED && quote.externalOrderErrorMessage && (
            <Card color={Color.DANGER} variant={Variant.SOFT}>
              <CardContent sx={{ whiteSpace: 'pre-wrap', fontSize: 'sm' }}>
                {quote.externalOrderErrorMessage.replace(/--/g, '\n--')}
              </CardContent>
            </Card>
          )}

          <Stepper
            orientation={Orientation.VERTICAL}
            sx={{
              '--Stepper-verticalGap': '16px',
              '--StepIndicator-size': '28px',
              '--Step-gap': '12px',
              '--Step-connectorInset': '4px',
              '--Step-connectorThickness': '2px',
              '--joy-palette-success-solidBg': theme.palette.success[400],

              [`& .${stepClasses.completed}`]: {
                '&::after': {
                  backgroundColor: theme.palette.success[400],
                },
              },

              [`& .${stepClasses.active} .${stepIndicatorClasses.root}`]: {
                boxShadow: 'none',
              },

              [`& .${stepClasses.active} .${stepIndicatorClasses.variantSolid}`]: {
                border: 'none',
                color: theme.palette.common.white,
              },

              [`& .${stepClasses.active} .${stepIndicatorClasses.variantOutlined}`]: {
                borderWidth: 2,
                borderColor: theme.palette.success[400],
                backgroundColor: theme.palette.background.surface,
              },
            }}
          >
            {steps.map((step, index) => (
              <Step
                key={`orderStep${index}`}
                completed={
                  step.status === OrderStepStatus.COMPLETED ||
                  step.status === OrderStepStatus.FAILED
                }
                active={step.status !== OrderStepStatus.PENDING}
                indicator={
                  <StepIndicator
                    variant={STEP_INDICATORS[step.status].variant}
                    color={STEP_INDICATORS[step.status].color}
                  >
                    {step.status === OrderStepStatus.COMPLETED && <Check />}
                    {step.status === OrderStepStatus.FAILED && <X />}
                    {step.status === OrderStepStatus.ACTIVE && (
                      <Box
                        sx={{
                          width: ACTIVE_STEP_DOT_SIZE,
                          height: ACTIVE_STEP_DOT_SIZE,
                          borderRadius: '50%',
                          backgroundColor: theme.palette.success[400],
                        }}
                      />
                    )}
                  </StepIndicator>
                }
              >
                {/* The color prop opts out of the theme's body-sm colour override. */}
                <Typography
                  level={Level.BODY_SM}
                  color={Color.NEUTRAL}
                  sx={{
                    color:
                      step.status === OrderStepStatus.PENDING
                        ? theme.palette.neutral[400]
                        : 'text.primary',
                    fontWeight: 600,
                  }}
                >
                  {step.label}
                </Typography>
              </Step>
            ))}
          </Stepper>
        </Stack>
      }
      open={open}
      arrow
      followCursor={false}
      placement={TooltipPosition.RIGHT_START}
      variant={Variant.OUTLINED}
      sx={{
        maxWidth: 400,
        p: 1.5,
        '--Tooltip-arrowSize': '12px',
      }}
    >
      {children}
    </Tooltip>
  );
};
